import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Link as MuiLink
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { paymentService, studentService } from '../../services/api';
import { getStatusIcon, getStatusColor, getStatusLabel } from '../../utils/statusFormatters';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { get } from 'http';
dayjs.locale('es');

interface PaymentDetails {
  id: number;
  student: any;
  parent: any;
  month: string;
  year: number;
  amount: number;
  total_due?: number;           // 👈 Agregar (monto total a pagar)
  paid_amount?: number;         // 👈 Agregar (monto acumulado pagado)
  pending_balance?: number;     // 👈 Agregar (saldo pendiente)
  is_completed?: boolean;       // 👈 Agregar (si está completado)
  payment_history?: any[];      // 👈 Agregar (historial de pagos parciales)
  last_partial_payment_date?: string; // 👈 Agregar
  payment_date: string;
  status: string;
  bank_reference?: string;
  comments?: string;
  payment_proof_url?: string;
  created_at: string;
  updated_at: string;
}

const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payment, setPayment] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  //const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [selectedProofUrl, setSelectedProofUrl] = useState<string | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [studentHistory, setStudentHistory] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);

   useEffect(() => {
      loadPaymentDetails();
    }, [id]); // Solo cuando cambia el ID

    // 👇 NUEVO: Actualizar paymentHistory cuando payment cambie (sin causar recarga)
    useEffect(() => {
      if (payment) {
        setPaymentHistory(payment.payment_history || []);
      }
    }, [payment]); 

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getMonthlyPayment(parseInt(id!));
      setPayment(response.data);
      
      // Cargar historial del estudiante
      if (response.data.student) {
        const historyResponse = await studentService.getPayments(response.data.student.id);
        setStudentHistory(historyResponse.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar detalles del pago');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await paymentService.deleteMonthly(parseInt(id!));
      navigate('/monthly-payments');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar pago');
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await paymentService.markAsPaid(parseInt(id!));
      loadPaymentDetails();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar estado');
    }
  };

  const handleSendEmail = () => {
    // Implementar envío de email con comprobante
    const subject = `Comprobante de Pago - ${payment?.student?.first_name} ${payment?.student?.last_name}`;
    const body = `Detalles del pago:\n` +
      `Estudiante: ${payment?.student?.first_name} ${payment?.student?.last_name}\n` +
      `Período: ${payment?.month} ${payment?.year}\n` +
      `Monto: ₽${payment?.amount}\n` +
      `Fecha: ${dayjs(payment?.payment_date).format('DD/MM/YYYY')}\n` +
      `Referencia: ${payment?.bank_reference || 'N/A'}`;
    
    window.location.href = `mailto:${payment?.parent?.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSendWhatsApp = () => {
    const phone = payment?.parent?.phone;
    if (!phone) return;
    
    const message = `*Comprobante de Pago*%0a` +
      `Estudiante: ${payment?.student?.first_name} ${payment?.student?.last_name}%0a` +
      `Período: ${payment?.month} ${payment?.year}%0a` +
      `Monto: ₽${payment?.amount}%0a` +
      `Fecha: ${dayjs(payment?.payment_date).format('DD/MM/YYYY')}`;
    
    window.open(`https://wa.me/${phone.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'paid':
        return {
          label: 'Pagado',
          color: 'success' as const,
          icon: <CheckCircleIcon />,
          bgColor: '#e8f5e8'
        };
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'warning' as const,
          icon: <ErrorIcon />,
          bgColor: '#fff4e5'
        };
      case 'baja':
        return {
          label: 'Baja',
          color: 'error' as const,
          icon: <WarningIcon />,
          bgColor: '#ffebee'
        };
      case 'finalizado':
        return {
          label: 'Finalizado',
          color: 'info' as const,
          icon: <CheckCircleIcon />,
          bgColor: '#e3f2fd'
        };
      case 'alta':
        return {
          label: 'Alta',
          color: 'secondary' as const,
          icon: <CheckCircleIcon />,
          bgColor: '#f3e5f5'
        };
      default:
        return {
          label: status,
          color: 'default' as const,
          icon: <ReceiptIcon />,
          bgColor: '#f5f5f5'
        };
    }
  };

  const formatMonth = (month: string) => {
    return month.charAt(0).toUpperCase() + month.slice(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Alert severity="error">
        {error || 'Pago no encontrado'}
      </Alert>
    );
  }

  //const statusInfo = getStatusInfo(payment.status);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/monthly-payments')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Detalles del Pago
          </Typography>
          <Chip
            icon={getStatusIcon(payment.status)}
            label={getStatusLabel(payment.status)}
            color={getStatusColor(payment.status)}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box>
          <Tooltip title="Enviar por Email">
            <IconButton 
              onClick={handleSendEmail}
              disabled={!payment?.parent?.email}
              sx={{ mr: 1 }}
            >
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar por WhatsApp">
            <IconButton 
              onClick={handleSendWhatsApp}
              disabled={!payment?.parent?.phone}
              sx={{ mr: 1 }}
            >
              <WhatsAppIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Imprimir">
            <IconButton sx={{ mr: 1 }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/monthly-payments/${id}/edit`)}
            sx={{ mr: 1 }}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Eliminar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Información del Estudiante */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Información del Estudiante
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate(`/students/${payment.student?.id}`)}
                >
                  Ver perfil
                </Button>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Nombre Completo"
                    secondary={`${payment.student?.first_name} ${payment.student?.last_name}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Grado"
                    secondary={payment.student?.grade || 'No especificado'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AttachMoneyIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Patrocinador"
                    secondary={payment.student?.sponsor || 'No tiene'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Información del Padre */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Padre/Tutor
                </Typography>
                {payment.parent && (
                  <Button
                    size="small"
                    onClick={() => navigate(`/parents/${payment.parent.id}`)}
                  >
                    Ver perfil
                  </Button>
                )}
              </Box>
              
              {payment.parent ? (
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Nombre"
                      secondary={payment.parent.spanish_name || payment.parent.russian_name}
                    />
                  </ListItem>
                  {payment.parent.email && (
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={
                          <MuiLink href={`mailto:${payment.parent.email}`}>
                            {payment.parent.email}
                          </MuiLink>
                        }
                      />
                    </ListItem>
                  )}
                  {payment.parent.phone && (
                    <ListItem>
                      <ListItemIcon>
                        <WhatsAppIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Teléfono"
                        secondary={
                          <MuiLink 
                            href={`https://wa.me/${payment.parent.phone.replace(/\+/g, '')}`}
                            target="_blank"
                          >
                            {payment.parent.country_code} {payment.parent.phone}
                          </MuiLink>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                <Alert severity="info">
                  No hay padre/tutor asociado a este pago
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles del Pago */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles del Pago
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ width: '30%', bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2">Período</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {formatMonth(payment.month)} {payment.year}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2">Monto</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" color="primary">
                          ₽{payment.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2">Fecha de Pago</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {dayjs(payment.payment_date).format('dddd, D [de] MMMM [de] YYYY')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Registrado: {dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {payment.bank_reference && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Referencia Bancaria</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {payment.bank_reference}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {payment.comments && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Comentarios</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {payment.comments}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2">Estado</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(payment.status)}
                          label={getStatusLabel(payment.status)}
                          color={getStatusColor(payment.status)}
                        />
                        {payment.status === 'pending' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={handleMarkAsPaid}
                            sx={{ ml: 2 }}
                          >
                            Marcar como Pagado
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Comprobante de Pago */}
        <Grid size={{ xs: 12}}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Comprobante de Pago
                </Typography>
                {payment.payment_proof_url && (
                  <Box>
                    <Button
                      startIcon={<DownloadIcon />}
                      variant="outlined"
                      size="small"
                      href={payment.payment_proof_url}
                      download
                      sx={{ mr: 1 }}
                    >
                      Descargar
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setSelectedProofUrl(payment.payment_proof_url || null)}
                    >
                      Ver Comprobante
                    </Button>
                  </Box>
                )}
              </Box>
              
              {payment.payment_proof_url ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#fafafa',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                  onClick={() => setSelectedProofUrl(payment.payment_proof_url || null)}
                >
                  {payment.payment_proof_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                    <Box
                      component="img"
                      src={payment.payment_proof_url}
                      alt="Comprobante"
                      sx={{
                        maxHeight: 200,
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Box>
                      <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1">
                        Ver archivo PDF de comprobante
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Haz clic para ver el documento completo
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ) : (
                <Alert severity="info">
                  No se ha subido ningún comprobante para este pago
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Historial del Estudiante */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Historial de Pagos del Estudiante
                </Typography>
                <Button
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  size="small"
                >
                  Ver Historial Completo
                </Button>
              </Box>
              
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <List dense>
                  {studentHistory
                    .filter(p => p.id !== payment.id)
                    .slice(0, 5)
                    .map((p) => (
                      <Button
                        //key={p.id}
                        //button
                        size="small"
                        onClick={() => navigate(`/monthly-payments/${p.id}`)}
                      >
                        <ListItemIcon>
                          <Chip
                            icon={p.status === 'paid' ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={p.status === 'paid' ? 'Pagado' : 'Pendiente'}
                            color={p.status === 'paid' ? 'success' : 'warning'}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${formatMonth(p.month)} ${p.year}`}
                          secondary={`Monto: ₽${p.amount} - ${dayjs(p.payment_date).format('DD/MM/YYYY')}`}
                        />
                      </Button>
                    ))}
                </List>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {paymentHistory.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Historial de Pagos Parciales
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Referencia</TableCell>
                        <TableCell align="center">Comprobante</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Acumulado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((partial: any) => (
                        <TableRow key={partial.id}>
                          <TableCell>{partial.id}</TableCell>
                          <TableCell>
                            {dayjs(partial.date).format('DD/MM/YYYY HH:mm')}
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="success.main" fontWeight="medium">
                              +₽{partial.amount.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>{partial.reference || '-'}</TableCell>
                          <TableCell align="center">
                            {partial.proof_url ? (
                              <Tooltip title="Ver comprobante de esta cuota">
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={() => setSelectedProofUrl(partial.proof_url)}
                                >
                                  <ReceiptIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>{partial.user?.name || 'Sistema'}</TableCell>
                          <TableCell>
                            ₽{partial.cumulative_total.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={6} align="right">
                          <strong>Total Pagado:</strong>
                        </TableCell>
                        <TableCell>
                          <strong>₽{payment.paid_amount?.toLocaleString()}</strong>
                        </TableCell>
                      </TableRow>
                      {payment.pending_balance != null && payment.pending_balance > 0 && (
                        <TableRow sx={{ bgcolor: 'warning.light' }}>
                          <TableCell colSpan={6} align="right">
                            <strong>Saldo Pendiente:</strong>
                          </TableCell>
                          <TableCell>
                            <strong>₽{payment.pending_balance.toLocaleString()}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>

      {/* Dialog para ver comprobante */}
      <Dialog
        open={!!selectedProofUrl}
        onClose={() => setSelectedProofUrl(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Comprobante de Pago</Typography>
            <IconButton onClick={() => setSelectedProofUrl(null)}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {payment?.payment_proof_url && (
            <Box sx={{ textAlign: 'center' }}>
              {payment.payment_proof_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={payment.payment_proof_url}
                  alt="Comprobante"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box sx={{ height: '70vh', width: '100%' }}>
                  <iframe
                    src={payment.payment_proof_url}
                    title="Comprobante PDF"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"           // ← fuerza que sea un <a> (opcional en MUI v5+, pero ayuda a TS)
            href={selectedProofUrl ?? '#'}   // evita undefined
            download                // o download="comprobante.pdf" si quieres nombre específico
            target="_blank"         // opcional: abre en nueva pestaña (recomendado para descargas)
            rel="noopener noreferrer" // seguridad cuando usas target="_blank"
            startIcon={<DownloadIcon />}
            size="small"
            disabled={!payment?.payment_proof_url} // muy buena práctica
            >
            Descargar
          </Button>
          <Button onClick={() => setSelectedProofUrl(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial completo */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Historial Completo de Pagos - {payment.student?.first_name} {payment.student?.last_name}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {studentHistory.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    selected={p.id === payment.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigate(`/monthly-payments/${p.id}`);
                      setHistoryDialogOpen(false);
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={`${formatMonth(p.month)} ${p.year}`}
                        size="small"
                        variant={p.id === payment.id ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">₽{p.paid_amount?.toLocaleString()}</TableCell>
                    <TableCell>
                      {p.payment_date ? dayjs(p.payment_date).format('DD [de] MMMM [de] YYYY') : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(p.status)}
                        label={getStatusLabel(p.status)}
                        color={getStatusColor(p.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro de eliminar este pago?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Estudiante:</strong> {payment.student?.first_name} {payment.student?.last_name}
            </Typography>
            <Typography variant="body2">
              <strong>Período:</strong> {formatMonth(payment.month)} {payment.year}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> ₽{payment.amount}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentDetail;